/**
 * Sentry-powered logging utility
 * Combines console logging for development with Sentry reporting for production
 */

import * as Sentry from '@sentry/react';

const isDevelopment = process.env.NODE_ENV === 'development';

type LogLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

interface UserContext {
  id: string;
  email: string;
  username?: string;
  displayName?: string;
}

/**
 * Format additional arguments for logging
 */
const formatContext = (args: any[]): Record<string, any> | undefined => {
  if (args.length === 0) return undefined;

  // If single object, use it directly
  if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
    return args[0];
  }

  // Otherwise, create array of additional data
  return { additionalData: args };
};

export const logger = {
  /**
   * Debug logging - only shows in development, adds breadcrumb in production
   */
  debug: (message: string, ...args: any[]) => {
    if (isDevelopment) {
      console.debug(message, ...args);
    }

    // Add breadcrumb for debugging trail
    Sentry.addBreadcrumb({
      message,
      level: 'debug',
      data: formatContext(args),
      timestamp: Date.now() / 1000,
    });
  },

  /**
   * Info logging - shows in development, adds breadcrumb in production
   */
  info: (message: string, ...args: any[]) => {
    if (isDevelopment) {
      console.info(message, ...args);
    }

    Sentry.addBreadcrumb({
      message,
      level: 'info',
      data: formatContext(args),
      timestamp: Date.now() / 1000,
    });
  },

  /**
   * Warning logging - logs to console and reports to Sentry
   */
  warn: (message: string, ...args: any[]) => {
    console.warn(message, ...args);

    // Use Sentry scope to add context
    Sentry.withScope(scope => {
      const context = formatContext(args);
      if (context) {
        scope.setContext('warning_context', context);
      }
      Sentry.captureMessage(message, 'warning');
    });
  },

  /**
   * Error logging - logs to console and reports to Sentry
   */
  error: (message: string, ...args: any[]) => {
    console.error(message, ...args);

    // Check if first argument is an Error object
    const error = args.find(arg => arg instanceof Error);
    const otherArgs = args.filter(arg => !(arg instanceof Error));

    // Use Sentry scope to add context
    Sentry.withScope(scope => {
      const context = formatContext(otherArgs);
      if (context) {
        scope.setContext('error_context', context);
      }

      // Capture error or message
      if (error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(message, 'error');
      }
    });
  },

  /**
   * Set user context for all subsequent logs
   */
  setUser: (user: UserContext | null) => {
    Sentry.setUser(user);
  },

  /**
   * Set user context for all subsequent logs (alias for setUser)
   */
  setUserContext: (user: UserContext | null) => {
    Sentry.setUser(user);
  },

  /**
   * Set tags for all subsequent logs
   */
  setTags: (tags: Record<string, string>) => {
    Sentry.setTags(tags);
  },

  /**
   * Set extra context for all subsequent logs
   */
  setContext: (key: string, context: any) => {
    Sentry.setContext(key, context);
  },

  /**
   * Clear current user and context
   */
  clearContext: () => {
    Sentry.setUser(null);
    // Breadcrumbs are automatically managed in newer Sentry versions
  },

  /**
   * Start a performance transaction (using breadcrumbs for compatibility)
   */
  startTransaction: (name: string, operation = 'navigation') => {
    // Add breadcrumb to track transaction start
    logger.addBreadcrumb(`Transaction started: ${name}`, { operation });

    // Return a simple transaction-like object for compatibility
    return {
      name,
      operation,
      startTime: Date.now(),
      finish: () => {
        logger.addBreadcrumb(`Transaction finished: ${name}`, {
          operation,
          duration: Date.now() - Date.now(),
        });
      },
    };
  },

  /**
   * Add breadcrumb manually
   */
  addBreadcrumb: (
    message: string,
    data: Record<string, any> = {},
    level: LogLevel = 'info'
  ) => {
    Sentry.addBreadcrumb({
      message,
      level,
      data,
      timestamp: Date.now() / 1000,
    });
  },

  /**
   * Capture a message with Sentry
   */
  captureMessage: (
    message: string,
    level: LogLevel = 'info',
    extra: Record<string, any> = {}
  ) => {
    Sentry.withScope(scope => {
      // Set context if extra data provided
      if (Object.keys(extra).length > 0) {
        scope.setContext('capture_context', extra);
      }
      Sentry.captureMessage(message, level);
    });
  },

  /**
   * Capture an exception with Sentry
   */
  captureException: (error: Error, extra: Record<string, any> = {}) => {
    Sentry.withScope(scope => {
      // Set context if extra data provided
      if (Object.keys(extra).length > 0) {
        scope.setContext('exception_context', extra);
      }
      Sentry.captureException(error);
    });
  },

  /**
   * Performance timing helper
   */
  time: (label: string) => {
    if (isDevelopment) {
      console.time(label);
    }
    logger.addBreadcrumb(`Timer started: ${label}`, {}, 'debug');
    return Date.now();
  },

  /**
   * Performance timing helper
   */
  timeEnd: (label: string, startTime?: number) => {
    if (isDevelopment) {
      console.timeEnd(label);
    }

    if (startTime) {
      const duration = Date.now() - startTime;
      logger.addBreadcrumb(`Timer ended: ${label}`, { duration }, 'debug');
      return duration;
    }
  },

  /**
   * Log authentication events
   */
  auth: {
    login: (userId: string, email: string) => {
      logger.info('User logged in', { userId, email });
      logger.setUser({ id: userId, email });
    },

    logout: () => {
      logger.info('User logged out');
      logger.clearContext();
    },

    error: (message: string, error?: Error) => {
      logger.error(`Auth error: ${message}`, error);
    },

    sessionExpired: () => {
      logger.warn('User session expired');
      logger.clearContext();
    },
  },

  /**
   * Log business events
   */
  business: {
    calculation: (organizationId: string, residentCount: number) => {
      logger.info('Calculation performed', { organizationId, residentCount });
    },

    reportGenerated: (calculationId: string, reportType: string) => {
      logger.info('Report generated', { calculationId, reportType });
    },

    configurationChanged: (section: string, changes: Record<string, any>) => {
      logger.info('Configuration changed', { section, changes });
    },
  },
};

// Re-export for compatibility
export default logger;
